import { useCallback, useReducer, useRef } from 'react'
import { includes } from 'ramda'
import effects from 'app/effects/effects'
import { z } from 'zod'
import axios from 'axios'
import { useUserProfile } from 'commons/useUserProfile'
import { getCreditsRequest } from './api'
import { localStorageService } from 'app/services/localStorageService/localStorageService'
import { isEmptyOrNil } from 'libs/empty'
import { usePlace } from 'commons/usePlace'
import { actionMessageType } from '../composeMessage/useComposeMessage'

const processAIContent = (content) => {
  if (!content) return content
  if (content.includes('style=')) {
    return content
  }
  // Add Tiptap-like styles to AI-generated content
  return content
    .replace(
      /<h([1-6])>/g,
      '<h$1 style="margin-top: 1.5rem; margin-bottom: 1rem; line-height: 1.38;">',
    )
    .replace(/<p>/g, '<p style="margin-bottom: 1rem;">')
    .replace(/<ol>/g, '<ol style="margin-bottom: 1rem;">')
    .replace(/<ul>/g, '<ul style="margin-bottom: 1rem;">')
    .replace(/<li>/g, '<li style="margin-bottom: 0.5rem;">')
    .replace(
      /<blockquote>/g,
      '<blockquote style="border-left: 4px solid #d1d5db; margin: 1.5rem 0; padding: 0.75rem 1.25rem; background-color: #f8fafc; border-radius: 0 8px 8px 0; font-style: italic; color: #475569;">',
    )
}

function reduce(state, action) {
  switch (action.type) {
    case 'open':
      return {
        ...state,
        step: 'prompting',
      }

    case 'close':
      return closeState(state)

    case 'topics-response':
      return {
        ...state,
        topics: action.topics,
      }

    case 'select-topic':
      return {
        ...state,
        selectedTopicIdx: action.topicIdx,
        selectedCustomPrompt: false,
      }

    case 'select-language':
      return {
        ...state,
        topics: undefined,
        language: action.language,
      }

    case 'generate-content':
      return {
        ...state,
        step: 'generating',
        prevStep: state.step,
      }

    case 'generate-content-response':
      if (!state.step) return state
      if (state.step === 'prompting')
        return { ...state, isExistAiTextPreview: false, isGoBackOpen: false }
      return {
        ...state,
        step: 'preview',
        title: action.title,
        body: action.body,
      }

    case 'generate-content-with-text':
      return {
        ...state,
        step: 'generating',
        prevStep: state.step,
        title: action.title,
        isOpenFromTitle: true,
      }

    case 'generate-content-with-text-response':
      return {
        ...state,
        step: 'preview',
        title: action.title,
        body: action.body,
      }

    case 'on-tone-change':
      return {
        ...state,
        tone: action.tone,
      }

    case 'on-improve-option-change':
      return {
        ...state,
        improveOption: action.improveOption,
      }

    case 'on-personalize-content-with-place-details-change':
      return {
        ...state,
        personalizeContentWithPlaceDetailsChecked: action.checked,
      }

    case 'on-message-length-change':
      return {
        ...state,
        selectedMessageLengthId: action.messageLengthId,
      }

    case 'on-custom-prompt-change':
      return { ...state, customPrompt: action.text }

    case 'on-write-custom-prompt-click':
      return {
        ...state,
        selectedTopicIdx: undefined,
        selectedCustomPrompt: true,
        customPrompt: state.customPrompt ?? '',
      }

    case 'cancel':
      if (!includes(state.step, ['preview', 'generating'])) return state
      return {
        ...state,
        isCancelGeneratingText: true,
      }

    case 'close-cancel':
      return {
        ...state,
        isCancelGeneratingText: false,
      }

    case 'back':
      if (state.step !== 'preview') return state
      return {
        ...state,
        isGoBackOpen: true,
      }

    case 'back-confirm':
      if (!includes(state.step, ['preview', 'generating', 'error']))
        return state
      return {
        ...state,
        step: 'prompting',
        isGoBackOpen: false,
        isExistAiTextPreview: false,
        isCancelGeneratingText: false,
        prevStep: state.step,
      }

    case 'back-cancel':
      if (state.step !== 'preview') return state
      return {
        ...state,
        isGoBackOpen: false,
      }

    case 'credits-response':
      return {
        ...state,
        credits: action.credits,
      }

    case 'show-error':
      return {
        ...state,
        step: 'error',
        error: action.error,
        prevStep: state.step,
      }
    case 'exist':
      if (!includes(state.step, ['preview', 'generating'])) return state
      return {
        ...state,
        isExistAiTextPreview: true,
      }
    case 'exist-confirm':
      if (!includes(state.step, ['preview', 'generating'])) return state
      return closeState(state)
    case 'close-exist':
      return {
        ...state,
        isExistAiTextPreview: false,
      }

    default:
      return state
  }
}

function closeState(state) {
  return {
    ...initialState,
    topics: state.topics,
    language: state.language,
  }
}

const initialState = {
  step: undefined,
  prevStep: undefined,
  topics: undefined,
  selectedTopicIdx: undefined,
  selectedCustomPrompt: false,
  customPrompt: '',
  error: undefined,
  language: undefined,
  tone: undefined,
  improveOption: undefined,
  personalizeContentWithPlaceDetailsChecked: false,
  selectedMessageLengthId: undefined,
  isGoBackOpen: false,
  isExistAiTextPreview: false,
  isCancelGeneratingText: false,
  credits: undefined,
  isOpenFromTitle: false,
}

const topicsResponseSchema = z.object({
  response: z.array(z.string()),
})

const contentResponseSchema = z.object({
  response: z.string(),
})

// Maybe use in the feture
// function useAILanguage() {
//   const getAllLanguage = useCallback(
//     () =>
//       effects.apis.ai
//         .languages({})
//         .then(R.over(R.lensProp('response'), sortLanguage)),
//     [],
//   )

//   const state = useFetcher(getAllLanguage, undefined)
//   const items = R.path(['values', 'response'])(state)
//   const itemsByCode = useMemo(() => {
//     if (!items) return new Map()
//     return items.reduce((map, item) => {
//       map.set(item.code, item)
//       return map
//     }, new Map())
//   }, [items])

//   return {
//     items,
//     itemsByCode,
//   }
// }

export function useGenerateTextWithAI(
  requestId,
  editorRef,
  onContentGeneratedWithAI,
) {
  const place = usePlace()
  const isChanging = place.aiBillingCharged
  // Maybe use in the feture
  // const languageData = useAILanguage()
  // const userLanguage = useMemo(() => {
  //   return (
  //     (userProfile &&
  //       userProfile.defaultLanguage &&
  //       userProfile.defaultLanguage[0]) ||
  //     undefined
  //   )
  // }, [userProfile])
  const [state, dispatch] = useReducer(reduce, initialState)
  const languageName = state.language ? state.language.name : undefined
  const cancellableRef = useRef(new AbortController())

  // useEffect(() => {
  //   if (state.language || !userLanguage || !languageData.itemsByCode.size)
  //     return

  //   dispatch({
  //     type: 'select-language',
  //     language: languageData.itemsByCode.has(userLanguage)
  //       ? userLanguage
  //       : languageData.itemsByCode.get('en'),
  //   })
  // }, [state.language, userLanguage, dispatch, languageData.itemsByCode])

  return {
    ...state,
    // languages: languageData.items,
    languages: [],
    isOpen: state.step !== undefined,
    onOpenClick: async function () {
      dispatch({ type: 'open' })
      cancelInflightRequest(cancellableRef)
      if (!localStorageService.getComposeAITour()) {
        dispatch({
          type: 'credits-response',
          credits: 999999,
        })
        const mockTopics = [
          'Back to School Tips for Parents of Young Learners',
          'Getting Involved: Prae Jecko Gecko Lover ParentTeacher Association',
          'Celebrating Thai Culture at Prae Jecko Gecko Lover',
          'Student Spotlight: Achievements at Our School',
          'Preparing for Higher Education: Resources for Our Students',
          'Fun and Educational Field Trip Opportunities in Bangkok',
        ]
        dispatch({
          type: 'topics-response',
          topics: mockTopics,
        })
        return
      }
      await Promise.all([
        state.topics === undefined
          ? generateTopics(languageName, requestId, dispatch, cancellableRef)
          : Promise.resolve(),
        getCredits(dispatch, cancellableRef),
      ])
    },
    onCloseClick: function () {
      cancelInflightRequest(cancellableRef)
      dispatch({ type: 'close' })
    },
    onCloseExist: () => {
      dispatch({ type: 'close-exist' })
    },
    onTopicClick: function (topicIdx) {
      dispatch({ type: 'select-topic', topicIdx })
    },
    onSelectLanguageCode: async function (languageCode) {
      // if (!languageData || !languageData.itemsByCode || !languageCode) return
      // const language = languageData.itemsByCode.get(languageCode)
      // if (!language) return
      // dispatch({ type: 'select-language', language })
      // generateTopics(language.name, state, dispatch, cancellableRef)
    },
    onGenerateWithTextClick: async function (text) {
      if (state.step) return
      dispatch({ type: 'generate-content-with-text', title: text })
      cancelInflightRequest(cancellableRef)
      await getCredits(dispatch, cancellableRef)
      await generateContentWithText(
        {
          text,
          tone: state.tone,
          type: state.improveOption,
          personalizeContentWithPlaceDetailsChecked:
            state.personalizeContentWithPlaceDetailsChecked,
          length: state.selectedMessageLengthId,
          language: undefined,
          request_id: requestId,
        },
        dispatch,
        cancellableRef,
      )
      await getCredits(dispatch, cancellableRef)
    },
    onGenerateContentClick: async function () {
      if (!includes(state.step, ['prompting', 'error'])) return
      const text =
        state.selectedTopicIdx !== undefined
          ? state.topics[state.selectedTopicIdx]
          : state.customPrompt
      dispatch({ type: 'generate-content' })
      cancelInflightRequest(cancellableRef)
      if (!localStorageService.getComposeAITour()) {
        dispatch({
          type: 'generate-content-response',
          title: text,
          body: ` Ready, Set, School (Without the Tears!) 🎉
The first-day jitters aren't just for kids! Parents, we're right there with you. Ease those back-to-school butterflies with these quick tips:

📚 Prep Week: Ditch the Sunday scaries! Read fun books about school, practice packing lunches, and visit the school together to build excitement.

🗣️ Communication is Key: Stay connected with your child's teacher. Share any concerns, celebrate successes, and foster a positive learning environment.

💪 Routine Power: Consistent bedtimes and morning routines are game-changers. Prepare the night before to make those school mornings smoother.

Let's make this school year the best one yet! 🙌 `,
        })
        return
      }

      await generateContentWithText(
        {
          text,
          tone: isEmptyOrNil(state.tone) ? null : state.tone,
          type: state.improveOption,
          personalizeContentWithPlaceDetailsChecked:
            state.personalizeContentWithPlaceDetailsChecked,
          length: state.selectedMessageLengthId,
          language: undefined,
          request_id: requestId,
        },
        dispatch,
        cancellableRef,
      )

      await getCredits(dispatch, cancellableRef)
    },

    onRegenerateContentClick: async function () {
      if (state.step !== 'preview') return
      dispatch({ type: 'generate-content' })
      cancelInflightRequest(cancellableRef)
      await regenerateContentWithText(
        {
          title: state.title,
          text: state.body,
          tone: state.tone,
          type: state.improveOption,
          language: undefined,
          request_id: requestId,
        },
        dispatch,
        cancellableRef,
      )
      await getCredits(dispatch, cancellableRef)
    },
    onUseContentClick: async function () {
      if (state.step !== 'preview' || !state.title || !state.body) return
      dispatch({ type: 'close' })
      editorRef?.current?.commands?.setContent(state.body, true)
      onContentGeneratedWithAI(state.title)
      dispatch({ type: actionMessageType.AI_GENERATE_TEXT_UPDATED })
    },
    onBackClick: function () {
      dispatch({ type: isChanging ? 'back' : 'back-confirm' })
    },
    onBackConfirmClick: function () {
      dispatch({ type: 'back-confirm' })
    },
    onBackCancelClick: function () {
      dispatch({ type: 'back-cancel' })
    },
    onToneChange: function (tone) {
      dispatch({ type: 'on-tone-change', tone })
    },
    onImproveOptionChange: function (improveOption) {
      if (state.step !== 'preview') return
      dispatch({ type: 'on-improve-option-change', improveOption })
    },
    onPersonalizeContentWithPlaceDetailsChange: function (checked) {
      dispatch({
        type: 'on-personalize-content-with-place-details-change',
        checked,
      })
    },
    onMessageLengthChange: function (messageLengthId) {
      dispatch({
        type: 'on-message-length-change',
        messageLengthId,
      })
    },
    onCustomPromptChange: function (text) {
      dispatch({
        type: 'on-custom-prompt-change',
        text,
      })
    },
    onWriteCustomPromptClick: function () {
      dispatch({
        type: 'on-write-custom-prompt-click',
      })
    },
    onExistClick: function () {
      dispatch({ type: isChanging ? 'exist' : 'exist-confirm' })
    },
    onExisTextConfirmClick: function () {
      cancelInflightRequest(cancellableRef)
      dispatch({ type: 'exist-confirm' })
    },
    onCancelClick: function () {
      dispatch({ type: isChanging ? 'cancel' : 'back-confirm' })
    },
    onCancelConfirmClick: function () {
      cancelInflightRequest(cancellableRef)
      dispatch({ type: 'back-confirm' })
    },
    onCloseCancel: () => {
      dispatch({ type: 'close-cancel' })
    },
  }
}

async function generateTopics(language, requestId, dispatch, cancellableRef) {
  try {
    const response = topicsResponseSchema.parse(
      await effects.apis.ai.topics({
        query: {
          language,
          request_id: requestId,
        },
        signal: cancellableRef.current.signal,
      }),
    )
    const topics = response.response
    dispatch({ type: 'topics-response', topics })
  } catch (error) {
    if (axios.isCancel(error)) return
    dispatch({ type: 'show-error', error })
  }
}

async function generateContentWithText(
  {
    text,
    type,
    tone,
    length,
    languageName,
    personalizeContentWithPlaceDetailsChecked,
    request_id,
  },
  dispatch,
  cancellableRef,
) {
  try {
    const response = contentResponseSchema.parse(
      await effects.apis.ai.content({
        data: {
          text,
          type,
          tone,
          length,
          included_place_info: personalizeContentWithPlaceDetailsChecked
            ? 1
            : 0,
          language: languageName,
          request_id,
        },
        signal: cancellableRef.current.signal,
      }),
    )
    dispatch({
      type: 'generate-content-response',
      title: text,
      body: response.response,
    })
  } catch (error) {
    if (axios.isCancel(error)) return
    dispatch({ type: 'show-error', error })
  }
}

async function regenerateContentWithText(
  { title, text, type, tone, languageName, request_id },
  dispatch,
  cancellableRef,
) {
  try {
    const response = contentResponseSchema.parse(
      await effects.apis.ai.content({
        data: {
          text,
          type,
          tone,
          language: languageName,
          request_id,
        },
        signal: cancellableRef.current.signal,
      }),
    )
    dispatch({
      type: 'generate-content-response',
      title,
      body: response.response,
    })
  } catch (error) {
    if (axios.isCancel(error)) return
    dispatch({ type: 'show-error', error })
  }
}

async function getCredits(dispatch, cancellableRef) {
  try {
    const response = await getCreditsRequest(cancellableRef)
    dispatch({
      type: 'credits-response',
      credits: response.credits,
    })
  } catch (error) {
    if (axios.isCancel(error)) return
  }
}

function cancelInflightRequest(cancellableRef) {
  cancellableRef.current.abort()
  cancellableRef.current = new AbortController()
}
